import BaseModal from 'components/atoms/base-modal';
import { useTranslation } from 'react-i18next';
import BaseSelect from 'components/molecules/select/base';
import { useSelectKeyNote } from 'infrastructure/hooks/key-notes/use-select-key-note-history';
import { DateFormats, ModuleType } from 'infrastructure/enums';
import { useEffect, useState } from 'react';
import { useGetKeyNoteHistory } from 'infrastructure/hooks/key-notes/use-get-key-note-history';
import { DateFormatter } from 'infrastructure/functions';
import { Skeleton } from 'antd';

import s from './styles.module.scss';
import { KeyNote } from '../key-note';

import type { ISelect } from 'infrastructure/hooks/key-notes/api/selectKeyNoteHistoryFn';
import type { IKeyNoteData } from 'infrastructure/interfaces';

interface IProps {
  isOpen: boolean;
  onCancel: () => void;
  note: IKeyNoteData;
  module: ModuleType;
  patientGuid: string;
}
interface ISelectItem {
  value: string;
  label: string;
}
export const HistoryModalKeyNote = ({
  isOpen,
  note,
  onCancel,
  module,
  patientGuid,
}: IProps) => {
  const { t } = useTranslation();
  const generateLabel = (index: number, len: number, date: Date | string) => {
    const formattedDate = DateFormatter({
      date,
      format: DateFormats['MM/dd/yy | h:mm a'],
    });
    if (index === 0) return `${t('labels.currentVersion')}`;

    if (index + 1 === len)
      return `${formattedDate} - ${t('labels.originalVersion')}`;

    return formattedDate;
  };

  const prepareDataToSelect = (data: ISelect[]) =>
    data.map((item, i) => ({
      value: item.guid,
      label: generateLabel(i, data.length, item.createdAt),
    }));

  const selectItems = useSelectKeyNote({
    module,
    patientGuid,
    noteGuid: note.guid,
  });
  const { data, isLoading } = selectItems[module === ModuleType.RPM ? 0 : 1];

  const preparedData = prepareDataToSelect([
    { guid: note.guid, createdAt: note.createdAt },
    ...(data ?? []),
  ]);
  const [keynote, setKeynote] = useState<ISelectItem | null>(preparedData[0]);

  const histories = useGetKeyNoteHistory({
    module,
    patientGuid,
    noteGuid: note.guid || '',
    historyGuid: keynote?.value || '',
  });

  const { data: history, isLoading: isLoadingHistory } =
    histories[module === ModuleType.RPM ? 0 : 1];

  const isSelectedCurrentNote = note.guid === keynote?.value;

  useEffect(() => {
    if (preparedData.length > 0 && !keynote) {
      setKeynote(preparedData[0]);
    }
  }, [preparedData, keynote]);

  const historyContent = () => {
    if (isSelectedCurrentNote) {
      return <KeyNote keynote={note} isInsideModal />;
    }
    if (isLoadingHistory) {
      return (
        <div className={s['history-loader-wrapper']}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      );
    }
    if (history) {
      return (
        <KeyNote
          keynote={{
            guid: note.guid,
            patientGuid: note.patientGuid,
            createdAt: note.createdAt,
            createdBy: note.createdBy,
            updatedBy: history.createdBy,
            expiresAt: history.expiresAt,
            updatedAt: history.createdAt,
            note: history.note,
          }}
          isInsideModal
        />
      );
    }
    return null;
  };

  return (
    <BaseModal
      title={t('labels.keyNoteHistoryVersion')}
      onCancel={onCancel}
      zIndex={3001}
      open={isOpen}
      width={600}
      bodyClassNames={s['history-modal-body']}
    >
      <div className={s['history-body-header']}>
        <h6 className={s['history-label-wrapper']}>
          {t('labels.keyNoteVersion')}
        </h6>
        <BaseSelect
          value={keynote}
          onChange={(newValues) => {
            const item = Array.isArray(newValues) ? newValues[0] : newValues;
            setKeynote(item);
          }}
          loading={isLoading}
          allowClear={false}
          options={preparedData || []}
        />
      </div>
      <div className={s['history-body']}>{historyContent()}</div>
    </BaseModal>
  );
};
